import { useEffect } from 'react';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.weartwice.com';

function setMetaContent(selector, content) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute('content', content);
}

function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/**
 * Sets a distinct document title + description/OG/Twitter meta + canonical
 * link for the current route, so each page gets its own crawlable metadata
 * instead of inheriting the homepage's from public/index.html. This matters
 * both for react-snap's prerendered snapshots (it captures whatever is in
 * <head> at crawl time) and for Google sitelinks, which need distinct
 * per-page titles to label separate entries.
 *
 * Restores the previous values on unmount so navigating back to a page that
 * doesn't call this hook (e.g. Home) isn't left with stale metadata.
 */
export default function usePageMeta({ title, description, path = '/' }) {
  useEffect(() => {
    const previous = {
      title: document.title,
      description: document.querySelector('meta[name="description"]')?.getAttribute('content'),
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content'),
      ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute('content'),
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute('content'),
      twitterTitle: document.querySelector('meta[property="twitter:title"]')?.getAttribute('content'),
      twitterDescription: document.querySelector('meta[property="twitter:description"]')?.getAttribute('content'),
      twitterUrl: document.querySelector('meta[property="twitter:url"]')?.getAttribute('content'),
      canonical: document.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };

    const url = `${SITE_URL}${path}`;

    document.title = title;
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="og:url"]', url);
    setMetaContent('meta[property="twitter:title"]', title);
    setMetaContent('meta[property="twitter:description"]', description);
    setMetaContent('meta[property="twitter:url"]', url);
    setCanonical(url);

    return () => {
      document.title = previous.title;
      if (previous.description != null) setMetaContent('meta[name="description"]', previous.description);
      if (previous.ogTitle != null) setMetaContent('meta[property="og:title"]', previous.ogTitle);
      if (previous.ogDescription != null) setMetaContent('meta[property="og:description"]', previous.ogDescription);
      if (previous.ogUrl != null) setMetaContent('meta[property="og:url"]', previous.ogUrl);
      if (previous.twitterTitle != null) setMetaContent('meta[property="twitter:title"]', previous.twitterTitle);
      if (previous.twitterDescription != null) setMetaContent('meta[property="twitter:description"]', previous.twitterDescription);
      if (previous.twitterUrl != null) setMetaContent('meta[property="twitter:url"]', previous.twitterUrl);
      if (previous.canonical != null) setCanonical(previous.canonical);
    };
  }, [title, description, path]);
}
